// F8 — «План к дедлайну и темп»: company-wide plan-to-deadline for the pool
// dashboard. Values are IDENTICAL for manager and admin (the backend computes
// them off the whole pool, not poolClause — see handlers_pool.ts handlePace):
// one number, no per-role skew. Viewer never sees this card.
//
// Goal (target_hours + deadline) is set by a MANAGER only; an admin sees a
// read-only version, or a modest placeholder while no goal exists yet.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError, type VistaPaceRes, type VistaStaffMe as Me } from '../api';
import type { T } from '../i18n';

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);

const fmtDate = (ymd: string): string => {
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
};

export function PaceCard({ me, t }: { me: Me; t: T }) {
  const canSee = me.role === 'manager' || me.role === 'admin';
  const [pace, setPace] = useState<VistaPaceRes | null | 'loading'>('loading');
  const [editing, setEditing] = useState(false);

  const load = () => {
    if (!canSee) return;
    api
      .pace()
      .then(setPace)
      .catch(() => setPace(null));
  };
  useEffect(load, [canSee]);

  if (!canSee) return null;
  if (pace === 'loading') return null;

  const hasGoal = pace != null && pace.targetHours != null && pace.deadline != null;

  return (
    <div class="panel">
      <div class="panel-head">
        <h3>{t('pace.title')}</h3>
        {me.role === 'manager' && hasGoal && !editing && (
          <button type="button" class="link" onClick={() => setEditing(true)}>
            {t('pace.edit')}
          </button>
        )}
      </div>

      {pace == null && (
        <p class="muted" style={{ fontSize: '13px' }}>
          {t('pace.loadFailed')}
        </p>
      )}

      {pace != null && (!hasGoal || editing) && me.role === 'manager' && (
        <GoalForm
          t={t}
          initial={pace}
          onSaved={(p) => {
            setPace(p);
            setEditing(false);
          }}
          onCancel={hasGoal ? () => setEditing(false) : undefined}
        />
      )}

      {pace != null && !hasGoal && me.role !== 'manager' && (
        <p class="muted" style={{ fontSize: '13px' }}>
          {t('pace.noGoal')}
        </p>
      )}

      {pace != null && hasGoal && !editing && <PaceBody pace={pace} t={t} />}
    </div>
  );
}

function PaceBody({ pace, t }: { pace: VistaPaceRes; t: T }) {
  const pct = Math.max(0, Math.min(100, Math.round((pace.acceptedHours / Math.max(pace.targetHours ?? 1, 1)) * 100)));
  const onTrack = pace.onTrack ?? false;
  const barColor = onTrack ? 'var(--green)' : 'var(--amber)';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '34px', fontWeight: 800, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums' }}>
            {fmtH(pace.requiredPerDay ?? 0)}
            <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ink3)' }}>
              {' '}
              {t('pace.perDay')}
            </span>
          </div>
          <div style={{ color: 'var(--ink3)', fontSize: '12px', fontWeight: 600 }}>{t('pace.need')}</div>
        </div>
        <span
          class="status-chip"
          style={{
            background: onTrack ? 'var(--green-soft)' : 'var(--amber-soft)',
            color: onTrack ? 'var(--green-press)' : 'var(--amber)',
          }}
        >
          {onTrack ? t('pace.onTrack') : t('pace.behind')}
        </span>
      </div>

      <div class="muted" style={{ fontSize: '13px', margin: '6px 0 12px' }}>
        {t('pace.until', { date: fmtDate(pace.deadline!), days: pace.daysRemaining ?? 0 })}{' '}
        <b>{fmtH(pace.recentPerDay)}</b> {t('pace.perDay7')}
      </div>

      <div style={{ height: '10px', borderRadius: 'var(--r-full)', background: 'var(--field)', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: barColor,
            borderRadius: 'var(--r-full)',
            transition: 'width .3s ease',
          }}
        />
      </div>
      <div class="muted" style={{ fontSize: '12px', marginTop: '6px' }}>
        {t('pace.accepted')} <b>{fmtH(pace.acceptedHours)}</b> {t('pace.of')}{' '}
        <b>{fmtH(pace.targetHours ?? 0)}</b> {t('pace.h')}
      </div>
    </>
  );
}

function GoalForm({
  t,
  initial,
  onSaved,
  onCancel,
}: {
  t: T;
  initial: VistaPaceRes;
  onSaved: (p: VistaPaceRes) => void;
  onCancel?: () => void;
}) {
  const [targetRaw, setTargetRaw] = useState(initial.targetHours != null ? String(initial.targetHours) : '');
  const [deadline, setDeadline] = useState(initial.deadline ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const targetHours = Number(targetRaw);
    if (!Number.isFinite(targetHours) || targetHours <= 0) {
      setError(t('pace.err.goal'));
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(deadline)) {
      setError(t('pace.err.deadline'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.setPaceGoal({ targetHours, deadline });
      const fresh = await api.pace();
      onSaved(fresh);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : t('pace.err.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {!initial.targetHours && (
        <p class="muted" style={{ fontSize: '13px', marginBottom: '10px' }}>
          {t('pace.intro')}
        </p>
      )}
      <div class="field">
        <span>{t('pace.goal')}</span>
        <input
          class="input"
          type="number"
          min="1"
          step="1"
          value={targetRaw}
          onInput={(e) => setTargetRaw((e.target as HTMLInputElement).value)}
          placeholder={t('pace.goalPh')}
        />
      </div>
      <div class="field">
        <span>{t('pace.deadline')}</span>
        <input
          class="input"
          type="date"
          value={deadline}
          onInput={(e) => setDeadline((e.target as HTMLInputElement).value)}
        />
      </div>
      {error && <div class="strip bad">{error}</div>}
      <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
        <button type="button" class="btn-primary" disabled={busy} onClick={submit}>
          {busy ? t('pace.saving') : t('pace.save')}
        </button>
        {onCancel && (
          <button type="button" class="btn-ghost" onClick={onCancel} disabled={busy}>
            {t('pace.cancel')}
          </button>
        )}
      </div>
    </div>
  );
}
